import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./FilterSelect.css";

export default function AutocompleteSelect({
  value,
  text,
  setText,
  options,
  getLabel,
  onSelect,
  onClear,
  placeholder,
  disabled,
  lockStyle = false,
  requireMatch = true,
  maxItems = 99999
}) {
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState([]);
  const [pos, setPos] = useState(null);
  const inputRef = useRef(null);

  const norm = (s) => String(s || "").replace(/\s+/g, "").toLowerCase();

  useEffect(() => {
    if (!open) return;
    const q = norm(text);
    const next = (options || [])
      .filter((o) => norm(getLabel(o)).includes(q))
      .slice(0, maxItems);
    setHits(next);
  }, [text, options, open, maxItems, getLabel]);

  useEffect(() => {
    if (!open) return;
    if (!text) setHits((options || []).slice(0, maxItems));
  }, [open, text, options, maxItems]);

  const updatePos = () => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: r.left,
      top: r.bottom,
      width: r.width
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePos();
  }, [open, hits.length]);

  useEffect(() => {
    if (!open) return;
    const onWin = () => updatePos();
    window.addEventListener("scroll", onWin, true);
    window.addEventListener("resize", onWin);
    return () => {
      window.removeEventListener("scroll", onWin, true);
      window.removeEventListener("resize", onWin);
    };
  }, [open]);

  const handleBlur = () => {
    // 클릭으로 선택하는 케이스 때문에 기존 120ms 유지
    setTimeout(() => {
      setOpen(false);

      if (disabled) return;

      const t = String(text || "").trim();
      if (!t) {
        onClear && onClear();
        return;
      }

      // ✅ 입력한 텍스트가 옵션 라벨과 "정확히" 같으면 자동 선택
      // const found = (options || []).find((o) => String(getLabel(o) || "").trim() === t);
      const found = (options || []).find((o) => norm(getLabel(o)) === norm(t)); //대소문자 무시
      if (!found) {
        if (requireMatch) {
          onClear && onClear()
        }
        return
      }

      onSelect(found);
    }, 120);
  };

  return (
    <div className="autocomplete" style={{ opacity: disabled ? 0.7 : 1 }}>
      <input
        ref={inputRef}
        className={lockStyle ? "field__control" : "field__control highlight"}
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          if (!open) setOpen(true);
          requestAnimationFrame(updatePos);
        }}
        onFocus={() => {
          setOpen(true);
          requestAnimationFrame(updatePos);
        }}
        onBlur={handleBlur}
        disabled={disabled}
      />

      {open && !disabled && hits.length > 0 && pos &&
        createPortal(
          <div
            className="autocomplete-menu portal"
            style={{ left: pos.left, top: pos.top, width: pos.width }}
          >
            {hits.map((o) => (
              <button
                key={o.id}
                type="button"
                className="autocomplete-item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const label = getLabel(o);
                  setText(label);
                  onSelect(o);
                  setOpen(false);
                }}
              >
                {getLabel(o)}
              </button>
            ))}
          </div>,
          document.body
        )
      }
    </div>
  );
}
